import { Card, ChevronLeft, ChevronRight, Search, cn } from "@app/ui";
import { useEffect, useState } from "react";
import { Navigate, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useI18n } from "../../lib/useI18n.js";
import { useMobileUx } from "../../lib/useMobileUx.js";
import { useSettingsStore } from "../../stores/index.js";
import { getLastSettingsRoute, rememberSettingsRoute } from "./last-settings-page.js";
import { SETTINGS_CATEGORIES, visibleCategories, type SettingsCategory } from "./nav.js";
import { searchSettings } from "./search-index.js";
import { useSettingsFocusStore } from "./useSettingsFocus.js";

/** Settings shell: a left rail (desktop) / drill-in list (mobile) of categories
 *  over the nested sub-pages, with a search box that jumps to + flashes any
 *  setting. Hydrates the settings store once so every sub-page reads fresh. */
export function SettingsRoute() {
  const { t } = useI18n();
  const hydrate = useSettingsStore((s) => s.hydrate);
  const developerMode = useSettingsStore((s) => s.developerMode);
  const mobile = useMobileUx();
  const active = useActiveCategory();
  useEffect(() => { void hydrate(); }, [hydrate]);
  useEffect(() => {
    if (active && visibleCategories(developerMode).some((c) => c.route === active.route)) {
      rememberSettingsRoute(active.route);
    }
  }, [active, developerMode]);

  return (
    <div className="mx-auto flex w-full max-w-7xl flex-col gap-5">
      {mobile && active ? (
        <MobileCategoryHeader category={active} />
      ) : (
        <h1 className="text-2xl font-semibold">{t("common.settings")}</h1>
      )}

      <SettingsSearch developerMode={developerMode} mobile={mobile} />

      {mobile ? (
        <Outlet />
      ) : (
        <div className="flex gap-6">
          <SettingsRail developerMode={developerMode} />
          <div className="min-w-0 flex-1">
            <Outlet />
          </div>
        </div>
      )}
    </div>
  );
}

/** Index route: redirect to General on desktop; show the drill-in list on mobile. */
export function SettingsIndex() {
  const { t } = useI18n();
  const mobile = useMobileUx();
  const developerMode = useSettingsStore((s) => s.developerMode);
  const routes = visibleCategories(developerMode).map((c) => c.route);
  if (!mobile) return <Navigate to={getLastSettingsRoute(routes) ?? "general"} replace />;
  return (
    <div className="flex flex-col gap-2">
      {visibleCategories(developerMode).map((c) => (
        <NavLink
          key={c.route}
          to={c.route}
          className="flex items-center gap-3 rounded-lg border bg-card p-3 text-left hover:bg-accent/50"
        >
          <c.icon className="h-5 w-5 shrink-0 text-muted-foreground" />
          <span className="min-w-0 flex-1">
            <span className="block text-sm font-medium">{t(c.labelKey)}</span>
            <span className="block truncate text-xs text-muted-foreground">
              {t(c.mobileBlurbKey ?? c.blurbKey)}
            </span>
          </span>
          <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
        </NavLink>
      ))}
    </div>
  );
}

function SettingsRail({ developerMode }: { developerMode: boolean }) {
  const { t } = useI18n();
  return (
    <nav className="sticky top-4 flex w-52 shrink-0 flex-col gap-0.5 self-start">
      {visibleCategories(developerMode).map((c) => (
        <NavLink
          key={c.route}
          to={c.route}
          className={({ isActive }) =>
            cn(
              "flex items-center gap-2.5 rounded-md px-3 py-2 text-sm",
              isActive ? "bg-accent font-medium" : "text-muted-foreground hover:bg-accent/50",
            )
          }
        >
          <c.icon className="h-4 w-4 shrink-0" />
          {t(c.labelKey)}
        </NavLink>
      ))}
    </nav>
  );
}

function MobileCategoryHeader({ category }: { category: SettingsCategory }) {
  const { t } = useI18n();
  const navigate = useNavigate();
  return (
    <div className="flex items-center gap-2">
      <button
        type="button"
        onClick={() => navigate("/settings")}
        aria-label={t("common.backToSettings")}
        className="-ml-1 flex h-9 w-9 items-center justify-center rounded-md hover:bg-accent"
      >
        <ChevronLeft className="h-5 w-5" />
      </button>
      <h1 className="text-xl font-semibold">{t(category.labelKey)}</h1>
    </div>
  );
}

function SettingsSearch({ developerMode, mobile }: { developerMode: boolean; mobile: boolean }) {
  const { t } = useI18n();
  const [query, setQuery] = useState("");
  const navigate = useNavigate();
  const focus = useSettingsFocusStore((s) => s.focus);
  const results = searchSettings(query, { developerMode, mobile });
  const labelByRoute = new Map(SETTINGS_CATEGORIES.map((c) => [c.route, t(c.labelKey)]));

  const go = (route: string, id: string) => {
    setQuery("");
    navigate(`/settings/${route}`);
    // Let the route mount before flashing the card.
    setTimeout(() => focus(id), 60);
  };

  return (
    <div className="relative">
      <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
      <input
        type="search"
        name="settings-search"
        autoComplete="off"
        autoCorrect="off"
        autoCapitalize="none"
        spellCheck={false}
        data-bwignore="true"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder={t("settings.search.placeholder")}
        className="w-full rounded-md border border-input bg-background py-2 pl-9 pr-3 text-sm outline-none focus:ring-1 focus:ring-ring"
      />
      {query.trim() !== "" && (
        <Card className="absolute z-20 mt-1 max-h-80 w-full overflow-auto p-1 shadow-md">
          {results.length === 0 ? (
            <p className="px-3 py-2 text-sm text-muted-foreground">{t("settings.search.none")}</p>
          ) : (
            results.map((r) => (
              <button
                key={`${r.route}-${r.id}`}
                type="button"
                onMouseDown={(e) => { e.preventDefault(); go(r.route, r.id); }}
                className="flex w-full items-center justify-between gap-3 rounded px-3 py-2 text-left text-sm hover:bg-accent"
              >
                <span className="truncate">{r.title}</span>
                <span className="shrink-0 text-xs text-muted-foreground">
                  {labelByRoute.get(r.route) ?? r.categoryLabel}
                </span>
              </button>
            ))
          )}
        </Card>
      )}
    </div>
  );
}

/** The category whose route matches the current path, or null at the index. */
function useActiveCategory(): SettingsCategory | null {
  const { pathname } = useLocation();
  const seg = pathname.replace(/^\/settings\/?/, "").split("/")[0];
  return SETTINGS_CATEGORIES.find((c) => c.route === seg) ?? null;
}
